import React, { useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { ConfirmProvider } from './components/ConfirmModal';
import AppRoutes from './routes';
import ErrorBoundary from './components/ErrorBoundary';
import OfflineBanner from './components/OfflineBanner';
import { queryClient } from './query/queryClient';

/**
 * App root. Architecture Rule 36: ErrorBoundary at top + react-hot-toast Toaster.
 * Frontend quality baseline: TanStack Query owns server state and cache.
 *
 * Provider order:
 *   ErrorBoundary -> QueryClientProvider -> AuthProvider -> BrowserRouter -> AppRoutes
 *   <Toaster /> and <OfflineBanner /> stay outside the route tree.
 */
export default function App() {
  useEffect(() => {
    if (window.__hideLoader) window.__hideLoader();
  }, []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <AuthProvider>
            <ConfirmProvider>
              <BrowserRouter>
                <AppRoutes />
              </BrowserRouter>
            </ConfirmProvider>
          </AuthProvider>
        </ThemeProvider>
        {process.env.REACT_APP_QUERY_DEVTOOLS === 'true' && (
          <ReactQueryDevtools initialIsOpen={false} />
        )}
      </QueryClientProvider>
      <OfflineBanner />
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          success: { duration: 3000 },
          error: { duration: 6000 },
        }}
      />
    </ErrorBoundary>
  );
}
